import { useEffect, useRef } from "react";

/**
 * True when the event originates from a text-entry element, so a global key
 * sequence should not hijack normal typing. Checks `isContentEditable` (real
 * browsers) and the `contenteditable` attribute (fallback; the property is not
 * implemented under jsdom).
 */
function isTextEntryTarget(target: EventTarget | null): boolean {
	if (!(target instanceof HTMLElement)) return false;
	const tag = target.tagName;
	if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
	if (target.isContentEditable) return true;
	const attr = target.getAttribute("contenteditable");
	return attr === "" || attr === "true";
}

/**
 * Subscribe to a global keyboard sequence. Attaches a single `keydown` listener
 * to `window` and invokes {@link onMatch} when `sequence` is entered in order.
 * A non-matching key resets progress, except a key equal to the first entry,
 * which restarts the match at the second position. The listener is removed on
 * unmount. `onMatch` may change between renders without re-binding the listener.
 *
 * @param sequence - ordered list of `KeyboardEvent.key` values to match. Must be
 *   a referentially stable reference (e.g. a module-level constant or a memoized
 *   value): the listener is re-bound and in-progress matching is reset whenever
 *   this reference changes, so passing a fresh inline array on every render would
 *   prevent any multi-key sequence from completing. An empty `sequence` is inert.
 * @param onMatch - called once each time the full sequence completes.
 */
export function useKeySequence(sequence: readonly string[], onMatch: () => void): void {
	const onMatchRef = useRef(onMatch);
	// Keep the ref current via an effect, not an assignment during render:
	// writing `ref.current` during render is a react-hooks/refs error.
	useEffect(() => {
		onMatchRef.current = onMatch;
	});

	useEffect(() => {
		// An empty sequence has no keys to match; without this guard the
		// `index === sequence.length` check below would be true immediately and
		// fire `onMatch` on every keypress.
		if (sequence.length === 0) return;
		let index = 0;
		function handleKeyDown(event: KeyboardEvent) {
			if (isTextEntryTarget(event.target)) return;
			if (event.key === sequence[index]) {
				index += 1;
			} else {
				// A wrong key resets — but if it is the first key, it begins a fresh
				// attempt, so progress becomes 1 rather than 0.
				index = event.key === sequence[0] ? 1 : 0;
			}
			if (index === sequence.length) {
				index = 0;
				onMatchRef.current();
			}
		}
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [sequence]);
}
